import type { Article } from "./types";

export const articlesPt: Record<string, Article> = {
  "what-is-basetao-spreadsheet": {
    slug: "what-is-basetao-spreadsheet",
    title: "O que é o Basetao Spreadsheet? Guia completo para iniciantes",
    description:
      "Descubra o que é o Basetao Spreadsheet, como funciona e como usá-lo para encontrar produtos, comparar vendedores e planejar envio e QC antes de pedir.",
    intro: [
      "Um Basetao Spreadsheet é uma base de dados de produtos organizada que ajuda os compradores a encontrar e comparar itens disponíveis através do agente de compras Basetao. Em vez de rolar por milhares de anúncios individuais, você pode navegar por um único documento estruturado que agrupa os produtos por categoria e inclui detalhes de apoio como informações do vendedor, referências de produtos e notas práticas.",
      "Este guia explica o que é o Basetao Spreadsheet, o que costuma conter, como usá-lo de forma eficaz e o que ter em mente antes de fazer um pedido. Ele foi escrito para iniciantes que querem uma visão clara e prática, sem enrolação promocional.",
    ],
    takeaways: [
      "O Basetao Spreadsheet agrupa os produtos em categorias claras como calçados, moletons, camisetas, jaquetas e acessórios.",
      "É uma ferramenta de pesquisa que costuma incluir detalhes do vendedor, referências de produtos e organização por categoria.",
      "Verifique sempre os preços, o estoque e a reputação do vendedor na plataforma oficial antes de pedir.",
      "Os custos de envio, as fotos QC e as políticas de armazém variam, então confirme as condições atuais.",
    ],
    sections: [
      {
        heading: "O que é o Basetao Spreadsheet?",
        paragraphs: [
          "O Basetao é um agente de compras que ajuda compradores internacionais a adquirir produtos de marketplaces chineses. Ele oferece serviços como realização de pedidos, inspeção de qualidade, armazenagem, consolidação e envio internacional. Como esses marketplaces contêm um número enorme de anúncios, encontrar produtos confiáveis pode levar tempo.",
          "O Basetao Spreadsheet foi criado para facilitar esse processo. É uma lista de produtos e referências organizada por categoria que os compradores podem usar para pesquisar o que está disponível. Pense nele como um mapa: não vende nada por si só, mas ajuda você a entender quais produtos existem, como são agrupados e onde procurar em seguida.",
        ],
      },
      {
        heading: "O que há dentro do Spreadsheet?",
        paragraphs: [
          "O conteúdo exato pode variar, mas um Basetao Spreadsheet típico inclui várias informações úteis organizadas para uma leitura rápida.",
        ],
        items: [
          "Categorias — produtos agrupados por tipo, como calçados, moletons, camisetas, jaquetas, calças, chapéus, bolsas, cintos e acessórios.",
          "Referências de produtos — identificadores ou nomes que ajudam a localizar um item específico.",
          "Informações do vendedor — detalhes que ajudam a comparar diferentes vendedores.",
          "Notas práticas — dicas sobre tamanhos, qualidade e o que observar antes de pedir.",
        ],
      },
      {
        heading: "Como usar o Spreadsheet passo a passo",
        paragraphs: [
          "Usar o spreadsheet é simples, mas seguir um processo claro ajuda a evitar erros comuns.",
        ],
        items: [
          "Abra o spreadsheet e revise a lista de categorias.",
          "Escolha uma categoria que corresponda ao que você procura.",
          "Anote as referências de produtos que deseja pesquisar.",
          "Compare as informações do vendedor e os detalhes do produto.",
          "Verifique os requisitos de envio e QC para o seu destino.",
          "Confirme os preços e a disponibilidade no Basetao antes de pedir.",
        ],
      },
      {
        heading: "Categorias de produtos que você pode explorar",
        paragraphs: [
          "O spreadsheet é organizado em torno de categorias de compra comuns que ajudam a restringir rapidamente sua busca.",
        ],
        items: [
          "Calçados",
          "Moletons e suéteres",
          "Camisetas",
          "Jaquetas",
          "Calças e shorts",
          "Chapéus",
          "Conjuntos",
          "Roupas íntimas",
          "Camisas esportivas",
          "Acessórios",
          "Bolsas",
          "Cintos",
          "Perfumes",
        ],
      },
      {
        heading: "Benefícios e limitações",
        paragraphs: [
          "Como qualquer ferramenta de pesquisa, o spreadsheet tem pontos fortes claros e algumas coisas que não consegue fazer.",
        ],
        items: [
          "Economiza tempo em comparação com buscar manualmente entre milhares de anúncios.",
          "Oferece uma estrutura de categorias clara e fácil de navegar.",
          "Ajuda na comparação de vendedores e na descoberta de produtos.",
          "Não garante disponibilidade nem preço — os detalhes podem mudar.",
          "As informações podem ficar desatualizadas, verifique antes de pedir.",
          "Não substitui a verificação oficial na plataforma Basetao.",
        ],
      },
      {
        heading: "Envio, armazém e QC",
        paragraphs: [
          "Os custos de envio dependem de fatores como peso, volume, destino e método escolhido. O armazém do Basetao guarda os itens após a compra, e as fotos de controle de qualidade (QC) ajudam a confirmar que um produto está correto antes do envio internacional. Como esses detalhes mudam com o tempo, verifique sempre as condições atuais na plataforma oficial.",
        ],
      },
    ],
    faq: [
      {
        q: "O que é o Basetao Spreadsheet?",
        a: "É uma lista organizada por categoria de produtos e referências que ajuda os compradores a pesquisar itens disponíveis através do agente de compras Basetao.",
      },
      {
        q: "O Basetao Spreadsheet é gratuito?",
        a: "A disponibilidade e o preço podem variar, então consulte a fonte de onde você o obteve para as informações mais atuais.",
      },
      {
        q: "Ainda preciso verificar os produtos no Basetao?",
        a: "Sim. Um spreadsheet é uma ajuda de pesquisa, não uma garantia. Confirme sempre preços, estoque e detalhes do vendedor na plataforma oficial antes de pedir.",
      },
      {
        q: "Quais categorias estão incluídas?",
        a: "As categorias comuns incluem calçados, moletons e suéteres, camisetas, jaquetas, calças e shorts, chapéus, conjuntos, roupas íntimas, camisas esportivas, acessórios, bolsas, cintos e perfumes.",
      },
    ],
    conclusion:
      "O Basetao Spreadsheet é um ponto de partida prático para quem quer pesquisar produtos disponíveis através do Basetao sem vasculhar manualmente anúncios intermináveis. Ao organizar os produtos em categorias claras e combiná-los com referências úteis, ele economiza tempo e ajuda a comparar opções. Trate-o como um guia, não como uma garantia, e verifique sempre os detalhes atuais antes de fazer um pedido.",
  },

  "how-to-use-basetao-spreadsheet": {
    slug: "how-to-use-basetao-spreadsheet",
    title: "Como usar o Basetao Spreadsheet: guia passo a passo",
    description:
      "Aprenda a usar o Basetao Spreadsheet passo a passo para navegar por categorias, comparar vendedores, planejar o envio e fazer seu primeiro pedido com confiança.",
    intro: [
      "Começar com o Basetao Spreadsheet é mais fácil do que parece, mas um processo claro faz a diferença entre um pedido tranquilo e uma experiência frustrante. Este guia acompanha cada etapa, desde abrir o spreadsheet até confirmar seu pedido.",
      "Você não precisa de ferramentas especiais nem de conhecimento técnico. Um pouco de paciência e atenção aos detalhes bastam para pesquisar produtos com eficiência e evitar os erros mais comuns de iniciante.",
    ],
    takeaways: [
      "Comece revisando as categorias e depois restrinja ao tipo de produto desejado.",
      "Compare os detalhes do vendedor e as referências antes de se comprometer.",
      "Planeje o envio e o QC com antecedência para evitar surpresas.",
      "Verifique sempre os preços e o estoque na plataforma oficial antes de pedir.",
    ],
    sections: [
      {
        heading: "Passo 1: abra e revise as categorias",
        paragraphs: [
          "O spreadsheet é organizado por categoria, então sua primeira tarefa é percorrer a lista e encontrar o grupo que corresponde ao que você quer. As categorias costumam incluir calçados, moletons, camisetas, jaquetas, calças, chapéus, bolsas, cintos e acessórios.",
          "Reserve um momento para observar toda a estrutura antes de mergulhar. Entender como os produtos são agrupados ajudará você a navegar muito mais rápido.",
        ],
      },
      {
        heading: "Passo 2: escolha uma categoria e anote as referências",
        paragraphs: [
          "Depois de escolher a categoria, anote as referências ou nomes de produtos que lhe interessam. São os identificadores que você usará para localizar o item exato no marketplace.",
        ],
        items: [
          "Anote ou copie a referência de cada item.",
          "Mantenha uma lista curta para comparar opções lado a lado.",
          "Preste atenção às notas de tamanho e qualidade, se incluídas.",
        ],
      },
      {
        heading: "Passo 3: compare vendedores",
        paragraphs: [
          "O spreadsheet pode incluir informações do vendedor que ajudam a decidir de quem comprar. Comparar vendedores em confiabilidade, resposta e precisão do produto pode evitar problemas futuros.",
        ],
      },
      {
        heading: "Passo 4: planeje o envio e o QC",
        paragraphs: [
          "Antes de pedir, verifique os métodos de envio disponíveis para o seu destino e decida se quer fotos de controle de qualidade. As fotos QC permitem confirmar que um item está correto antes de sair do armazém, o que é especialmente útil para roupas e calçados.",
        ],
      },
      {
        heading: "Passo 5: verifique e peça",
        paragraphs: [
          "Por fim, verifique os preços atuais, a disponibilidade e a reputação do vendedor na plataforma oficial. Os preços e o estoque mudam com frequência, então trate o spreadsheet como um guia, não como um catálogo ao vivo. Quando tudo estiver certo, você pode pedir com confiança.",
        ],
      },
    ],
    faq: [
      {
        q: "Preciso me cadastrar antes de usar o spreadsheet?",
        a: "O spreadsheet em si é apenas um documento de referência. Para pedir de verdade, você precisará de uma conta Basetao na plataforma oficial.",
      },
      {
        q: "Com que frequência o spreadsheet é atualizado?",
        a: "A frequência varia conforme a fonte. Sempre confira a plataforma ao vivo para os preços e a disponibilidade mais atuais.",
      },
      {
        q: "Qual é a forma mais fácil de evitar erros?",
        a: "Siga os passos em ordem, anote as referências e verifique cada detalhe na plataforma oficial antes de pagar.",
      },
      {
        q: "Devo usar fotos QC?",
        a: "Para roupas e calçados, as fotos QC geralmente valem a pena porque permitem confirmar tamanho e aparência antes do envio internacional.",
      },
    ],
    conclusion:
      "Usar o Basetao Spreadsheet se resume a uma rotina simples: revisar categorias, anotar referências, comparar vendedores, planejar envio e QC e verificar antes de pedir. Siga esses passos e você passará menos tempo procurando e mais tempo decidindo com confiança.",
  },

  "basetao-spreadsheet-sellers": {
    slug: "basetao-spreadsheet-sellers",
    title: "Guia de vendedores do Basetao Spreadsheet: como compará-los",
    description:
      "Aprenda a avaliar e comparar vendedores no Basetao Spreadsheet para escolher opções confiáveis e evitar erros comuns de compra.",
    intro: [
      "Escolher o vendedor certo é uma das decisões mais importantes ao comprar através do Basetao. Um bom vendedor economiza tempo e dinheiro, enquanto um ruim pode levar a itens errados, atrasos ou devoluções difíceis.",
      "Este guia explica o que observar ao comparar vendedores no Basetao Spreadsheet e como tomar uma decisão com a qual você se sinta tranquilo.",
    ],
    takeaways: [
      "Compare vendedores por precisão, resposta e confiabilidade, não apenas por preço.",
      "Verifique se um vendedor fornece referências claras e detalhes de tamanho.",
      "Use fotos QC e avaliações quando disponíveis para reduzir o risco.",
      "Verifique o status atual do vendedor na plataforma oficial antes de pedir.",
    ],
    sections: [
      {
        heading: "Por que a escolha do vendedor importa",
        paragraphs: [
          "Diferentes vendedores podem oferecer o mesmo produto com diferentes níveis de qualidade, preço e disponibilidade. O vendedor que você escolher afeta o que recebe, quanto tempo leva e a facilidade de resolver qualquer problema.",
        ],
      },
      {
        heading: "O que comparar",
        paragraphs: [
          "Ao revisar vendedores no spreadsheet, concentre-se nos detalhes que realmente afetam seu pedido.",
        ],
        items: [
          "Precisão do produto — o anúncio corresponde ao item real?",
          "Referências claras — os identificadores são específicos o suficiente?",
          "Resposta e comunicação — o vendedor é fácil de contatar?",
          "Notas de tamanho e qualidade — há observações úteis?",
          "Avaliações — o que dizem outros compradores?",
        ],
      },
      {
        heading: "Sinais de um vendedor confiável",
        paragraphs: [
          "Vendedores confiáveis tendem a fornecer informações consistentes e detalhadas e a responder com clareza. Procure vendedores que listam referências precisas, incluem orientação de tamanho e têm um histórico descrito positivamente por outros compradores.",
        ],
      },
      {
        heading: "Erros comuns a evitar",
        paragraphs: [
          "Os erros mais comuns vêm de perseguir o menor preço sem verificar o vendedor. Isso pode levar a tamanhos errados, qualidade inferior ou itens que diferem das fotos.",
        ],
        items: [
          "Escolher apenas pelo preço sem comparar detalhes.",
          "Pular as fotos QC em itens onde o caimento e a aparência importam.",
          "Ignorar as notas de tamanho e pedir o tamanho errado.",
          "Não verificar o status atual do vendedor antes de pagar.",
        ],
      },
    ],
    faq: [
      {
        q: "Como sei se um vendedor é confiável?",
        a: "Procure referências precisas, detalhes de tamanho claros, comunicação responsiva e feedback positivo de outros compradores.",
      },
      {
        q: "Devo sempre escolher o vendedor mais barato?",
        a: "Não necessariamente. O menor preço pode vir com qualidade inferior ou suporte mais fraco, então compare o panorama completo antes de decidir.",
      },
      {
        q: "As fotos QC ajudam na escolha do vendedor?",
        a: "Sim, as fotos QC confirmam que o item real corresponde ao anúncio antes do envio, o que reduz o risco com vendedores desconhecidos.",
      },
      {
        q: "Posso trocar de vendedor depois de pedir?",
        a: "As políticas variam, então consulte as regras atuais na plataforma oficial se precisar mudar algo.",
      },
    ],
    conclusion:
      "A escolha do vendedor pode definir sua experiência com o Basetao. Ao comparar precisão, comunicação e confiabilidade em vez de apenas o preço, e usar fotos QC onde importa, você compra com muito mais confiança.",
  },

  "basetao-spreadsheet-qc": {
    slug: "basetao-spreadsheet-qc",
    title: "Guia QC do Basetao Spreadsheet: fotos de controle de qualidade explicadas",
    description:
      "Entenda como funcionam as fotos de controle de qualidade (QC) do Basetao, quando pedi-las e como elas ajudam a confirmar produtos antes do envio.",
    intro: [
      "As fotos de controle de qualidade são um dos recursos mais valiosos ao comprar através do Basetao. Elas mostram o item real que você está comprando antes do envio internacional, o que pode evitar erros caros.",
      "Este guia explica o que são as fotos QC, quando usá-las e como interpretar o que você vê.",
    ],
    takeaways: [
      "As fotos QC mostram o item real no armazém antes do envio.",
      "São especialmente úteis para roupas, calçados e itens onde o caimento e a aparência importam.",
      "Revise as fotos com cuidado quanto a tamanho, cor e defeitos antes de aprovar o envio.",
      "Peça o QC com antecedência para ter tempo de resolver problemas.",
    ],
    sections: [
      {
        heading: "O que são fotos QC?",
        paragraphs: [
          "Fotos de controle de qualidade são imagens tiradas do seu item depois que ele chega ao armazém do Basetao e antes de ser embalado para o envio internacional. Elas dão a chance de confirmar que o que você recebeu corresponde ao que pediu.",
        ],
      },
      {
        heading: "Quando pedir fotos QC",
        paragraphs: [
          "As fotos QC são mais úteis quando a aparência, o tamanho ou o estado de um item importam muito.",
        ],
        items: [
          "Roupas e calçados, onde tamanho e cor são fáceis de errar.",
          "Itens com marca visível ou detalhes de design específicos.",
          "Produtos frágeis ou de alto valor.",
          "Primeiros pedidos de um vendedor que você nunca usou.",
        ],
      },
      {
        heading: "Como revisar as fotos QC",
        paragraphs: [
          "Ao receber suas fotos QC, revise-as com cuidado em vez de olhar rapidamente. Compare o item com o anúncio e procure qualquer coisa que não corresponda.",
        ],
        items: [
          "Verifique a etiqueta de tamanho e as medidas, se fornecidas.",
          "Compare cores e detalhes de design com o anúncio original.",
          "Procure defeitos, manchas ou danos visíveis.",
          "Confirme que a quantidade e a variação corretas chegaram.",
        ],
      },
      {
        heading: "O que fazer se algo estiver errado",
        paragraphs: [
          "Se as fotos QC revelarem um problema, contate o suporte do Basetao antes de aprovar o envio. Em muitos casos, os problemas podem ser resolvidos com o vendedor ou o item pode ser trocado enquanto ainda está no armazém, o que é muito mais fácil do que lidar com um problema após o envio internacional.",
        ],
      },
    ],
    faq: [
      {
        q: "As fotos QC são gratuitas?",
        a: "As políticas variam e alguns planos podem cobrar uma pequena taxa. Consulte as condições atuais na plataforma oficial.",
      },
      {
        q: "Quantas fotos receberei?",
        a: "O número pode variar conforme o serviço e o item. Confirme o que está incluído ao pedir o QC.",
      },
      {
        q: "As fotos QC podem atrasar meu pedido?",
        a: "Elas adicionam uma pequena etapa antes do envio, mas o atraso costuma ser menor em comparação ao risco de receber um item errado.",
      },
      {
        q: "E se o item sair com defeito nas fotos?",
        a: "Contate o suporte imediatamente e não aprove o envio até que o problema seja resolvido.",
      },
    ],
    conclusion:
      "As fotos QC são uma forma simples, mas poderosa, de se proteger ao comprar através do Basetao. Peça-as para itens onde o caimento e a aparência importam, revise-as com cuidado e aja rapidamente se algo parecer errado.",
  },

  "basetao-spreadsheet-shipping": {
    slug: "basetao-spreadsheet-shipping",
    title: "Guia de envio do Basetao Spreadsheet: métodos, tempo e rastreamento",
    description:
      "Um guia claro sobre os métodos de envio do Basetao, quanto tempo leva o envio e como rastrear seu pedido do armazém até sua porta.",
    intro: [
      "O envio é uma das partes do processo do Basetao que os compradores novos acham mais confusa. Há vários métodos, prazos diferentes e vários fatores que afetam o custo e a velocidade.",
      "Este guia explica o básico do envio do Basetao para que você possa escolher um método que se ajuste ao seu orçamento e à rapidez com que precisa do item.",
    ],
    takeaways: [
      "Método, peso, volume e destino afetam o custo e a velocidade.",
      "Métodos mais rápidos custam mais, mas chegam antes; os lentos economizam dinheiro.",
      "A consolidação combina vários itens em um envio para reduzir o custo.",
      "Verifique sempre as tarifas e prazos atuais antes de pedir.",
    ],
    sections: [
      {
        heading: "Como funciona o envio do Basetao",
        paragraphs: [
          "Depois que seus itens chegam ao armazém e passam pelo QC, eles são embalados e enviados ao seu endereço. O Basetao oferece uma variedade de métodos, de opções econômicas mais lentas a opções expressas que chegam em questão de dias.",
        ],
      },
      {
        heading: "Métodos de envio comuns",
        paragraphs: [
          "As opções exatas podem mudar, mas a maioria dos compradores encontra algumas categorias amplas.",
        ],
        items: [
          "Linhas econômicas — a opção mais barata, com prazos mais longos.",
          "Linhas padrão — um equilíbrio entre custo e velocidade.",
          "Linhas expressas — entrega mais rápida a um preço mais alto.",
          "Linhas especializadas — opções para destinos ou tipos de produto específicos.",
        ],
      },
      {
        heading: "Consolidação e reembalagem",
        paragraphs: [
          "Se você comprar vários itens de vendedores diferentes, o Basetao pode consolidá-los em um único pacote. Isso reduz o número de envios e, muitas vezes, o custo total. A reembalagem também pode reduzir o volume, o que importa porque alguns métodos cobram por peso volumétrico.",
        ],
      },
      {
        heading: "Rastrear seu pedido",
        paragraphs: [
          "Quando seu pacote for enviado, você receberá um número de rastreamento. As atualizações podem levar um ou dois dias para aparecer e podem pausar durante o trânsito internacional. Guarde seu número de rastreamento e consulte a plataforma oficial para o status mais recente.",
        ],
      },
      {
        heading: "O que afeta o tempo de envio",
        paragraphs: [
          "O tempo de trânsito depende do método escolhido, do seu destino e do despacho aduaneiro. Temporadas de pico também podem adicionar atrasos, então planeje com antecedência se precisar de um item para uma data específica.",
        ],
      },
    ],
    faq: [
      {
        q: "Quanto tempo leva o envio do Basetao?",
        a: "Depende do método e do destino. As opções expressas chegam mais rápido, enquanto as linhas econômicas demoram mais. Consulte as estimativas atuais antes de pedir.",
      },
      {
        q: "O que é consolidação?",
        a: "A consolidação combina vários itens em um envio, o que pode reduzir o custo total de envio.",
      },
      {
        q: "Como o custo de envio é calculado?",
        a: "O custo geralmente se baseia no peso real ou volumétrico, no destino e no método escolhido.",
      },
      {
        q: "Posso rastrear meu pacote?",
        a: "Sim, você receberá um número de rastreamento após o envio. As atualizações podem pausar durante o trânsito internacional.",
      },
    ],
    conclusion:
      "Entender o envio do Basetao ajuda a escolher o equilíbrio certo entre custo e velocidade. Considere a consolidação para vários itens, observe o peso volumétrico e confirme sempre as tarifas e prazos atuais antes de pedir.",
  },

  "basetao-shipping-cost": {
    slug: "basetao-shipping-cost",
    title: "Custo de envio do Basetao: guia completo dos fatores de preço",
    description:
      "Aprenda o que afeta o custo de envio do Basetao, do peso e volume ao destino e método, e como estimar seu total antes de pedir.",
    intro: [
      "O custo de envio do Basetao raramente é um número fixo. Vários fatores determinam o que você paga, e entendê-los ajuda a evitar surpresas no pagamento.",
      "Este guia detalha os principais fatores de custo e oferece formas práticas de manter sua fatura de envio sob controle.",
    ],
    takeaways: [
      "Peso, volume, destino e método são os principais fatores de custo.",
      "O peso volumétrico pode tornar itens volumosos mais caros do que os pesados.",
      "A consolidação e a reembalagem costumam reduzir o custo total.",
      "As tarifas mudam, então verifique os preços atuais antes de pedir.",
    ],
    sections: [
      {
        heading: "Os principais fatores que afetam o custo",
        paragraphs: [
          "O custo de envio é calculado a partir de vários dados. Conhecê-los ajuda a prever e reduzir o que você paga.",
        ],
        items: [
          "Peso real — o peso físico do pacote.",
          "Peso volumétrico — o espaço que o pacote ocupa.",
          "Destino — enviar para algumas regiões custa mais.",
          "Método de envio — opções mais rápidas custam mais.",
          "Valor declarado — pode afetar taxas e seguro.",
          "Serviços extras — como reembalagem ou seguro.",
        ],
      },
      {
        heading: "Peso real vs peso volumétrico",
        paragraphs: [
          "As transportadoras costumam cobrar pelo maior dos dois: peso real ou volumétrico. O peso volumétrico é calculado a partir das dimensões do pacote, então um item leve, mas volumoso, pode custar tanto quanto um pesado e compacto. Reembalar para reduzir o volume pode, portanto, economizar dinheiro de verdade.",
        ],
      },
      {
        heading: "Como reduzir o custo de envio",
        paragraphs: [
          "Há várias formas práticas de reduzir sua fatura sem comprometer demais a velocidade.",
        ],
        items: [
          "Consolide vários itens em um único envio.",
          "Peça reembalagem para reduzir o volume.",
          "Escolha um método mais lento quando o tempo não for crítico.",
          "Compare os métodos disponíveis para o seu destino.",
        ],
      },
      {
        heading: "Estimar seu total",
        paragraphs: [
          "Antes de pedir, use a calculadora de envio oficial do Basetao ou a ferramenta de estimativa, se disponível, e confirme as tarifas para seu destino e método. Lembre-se de que sobretaxas sazonais e ajustes de combustível podem mudar os preços, então trate qualquer estimativa como aproximada.",
        ],
      },
    ],
    faq: [
      {
        q: "Quanto custa o envio do Basetao?",
        a: "Não há uma resposta única porque o custo depende do peso, volume, destino e método. Consulte as tarifas atuais para sua situação.",
      },
      {
        q: "Por que um pacote leve custa tanto?",
        a: "Ele pode ser cobrado por peso volumétrico, ou seja, o pacote ocupa mais espaço do que seu peso real sugere.",
      },
      {
        q: "A consolidação pode economizar dinheiro?",
        a: "Sim, combinar itens em um envio geralmente reduz o custo total em comparação com enviar cada item separadamente.",
      },
      {
        q: "Os preços de envio mudam?",
        a: "Sim, as tarifas podem mudar por demanda sazonal, ajustes de combustível e preços das transportadoras. Verifique sempre antes de pedir.",
      },
    ],
    conclusion:
      "O custo de envio do Basetao é determinado por peso, volume, destino e método. Ao entender o peso volumétrico e usar consolidação e reembalagem, você mantém os custos razoáveis e evita surpresas.",
  },

  "basetao-warehouse": {
    slug: "basetao-warehouse",
    title: "Guia do armazém do Basetao: armazenagem, QC e consolidação",
    description:
      "Aprenda como funciona o armazém do Basetao, do recebimento e armazenagem às fotos QC, consolidação e preparação do envio.",
    intro: [
      "O armazém do Basetao é o centro onde seus itens são recebidos, inspecionados, armazenados e, por fim, preparados para o envio internacional. Entender como funciona ajuda a usar o serviço de forma mais eficaz.",
      "Este guia percorre cada etapa do processo do armazém para que você saiba o que acontece após fazer seu pedido.",
    ],
    takeaways: [
      "O armazém recebe, armazena e inspeciona seus itens após a compra.",
      "As fotos QC são tiradas no armazém para você verificar os produtos.",
      "A consolidação e a reembalagem acontecem aqui antes do envio.",
      "A armazenagem costuma ser gratuita por um período limitado; depois podem ser aplicadas taxas.",
    ],
    sections: [
      {
        heading: "O que o armazém faz",
        paragraphs: [
          "Depois que um vendedor envia seu item, ele chega ao armazém do Basetao. Lá, a equipe o registra, armazena e o deixa disponível para inspeção, consolidação e embalagem final.",
        ],
      },
      {
        heading: "O processo do armazém passo a passo",
        paragraphs: [
          "O percurso do seu item pelo armazém segue um caminho previsível.",
        ],
        items: [
          "Pedido realizado — o vendedor envia seu item.",
          "O armazém recebe e registra o item.",
          "Armazenagem — o item aguarda novas instruções.",
          "QC — fotos opcionais confirmam o estado e os detalhes.",
          "Consolidação — vários itens são combinados.",
          "Preparação do envio — embalagem e geração da etiqueta.",
        ],
      },
      {
        heading: "Períodos de armazenagem e taxas",
        paragraphs: [
          "A maioria dos serviços de armazém oferece um período de armazenagem gratuito, após o qual podem ser aplicadas taxas diárias ou mensais. Se você planeja comprar itens ao longo de vários dias antes do envio, verifique quanto dura a armazenagem gratuita para consolidar dentro desse prazo.",
        ],
      },
      {
        heading: "Consolidação e reembalagem",
        paragraphs: [
          "A consolidação combina itens de pedidos diferentes em um pacote. A reembalagem remove o excesso de embalagem para reduzir volume e custo de envio. Ambas são feitas no armazém e são úteis quando você compra de vários vendedores.",
        ],
      },
    ],
    faq: [
      {
        q: "A armazenagem é gratuita?",
        a: "Geralmente há um período de armazenagem gratuito, após o qual podem ser aplicadas taxas. Consulte a política atual na plataforma oficial.",
      },
      {
        q: "O que é consolidação?",
        a: "A consolidação combina vários itens em um único envio, o que pode reduzir o custo total de envio.",
      },
      {
        q: "Onde as fotos QC são tiradas?",
        a: "As fotos QC são tiradas no armazém após a chegada do seu item e antes do envio.",
      },
      {
        q: "Posso armazenar itens enquanto continuo comprando?",
        a: "Sim, o armazém pode guardar itens por um período para que você consolide um pedido maior antes do envio.",
      },
    ],
    conclusion:
      "O armazém do Basetao é mais do que armazenagem. É onde seus itens são inspecionados, fotografados, consolidados e preparados para o envio. Entender cada etapa ajuda a planejar pedidos e economizar no envio.",
  },

  "basetao-coupons": {
    slug: "basetao-coupons",
    title: "Guia de cupons e códigos de desconto do Basetao: como economizar",
    description:
      "Aprenda como funcionam os cupons e códigos de desconto do Basetao, onde encontrar ofertas atuais e como verificar um código antes de usá-lo.",
    intro: [
      "Cupons e códigos de desconto podem reduzir o custo de comprar através do Basetao, mas nem sempre estão disponíveis e têm regras. Saber como funcionam ajuda a usá-los com honestidade e eficácia.",
      "Este guia explica onde as ofertas podem aparecer, como verificar se um código é válido e o que observar.",
    ],
    takeaways: [
      "Cupons e códigos podem aparecer periodicamente, mas a disponibilidade muda com frequência.",
      "Verifique sempre se um código ainda é válido antes de confiar nele.",
      "As ofertas podem ter restrições de elegibilidade, validade e valor mínimo.",
      "Nunca compartilhe nem use códigos de sites de terceiros não confiáveis.",
    ],
    sections: [
      {
        heading: "Como funcionam os cupons do Basetao",
        paragraphs: [
          "Um cupom ou código de desconto é uma sequência curta ou uma oferta que reduz o custo de um serviço, como uma porcentagem de desconto em uma taxa ou um desconto no envio. Os códigos costumam ser inseridos em um ponto específico do pagamento ou da área da conta.",
        ],
      },
      {
        heading: "Onde as ofertas podem aparecer",
        paragraphs: [
          "Os descontos nem sempre estão ativos, mas quando estão, podem aparecer em alguns lugares comuns.",
        ],
        items: [
          "O site oficial do Basetao e o painel da conta.",
          "Boletins ou anúncios oficiais.",
          "Promoções sazonais e eventos especiais.",
          "Canais oficiais de redes sociais.",
        ],
      },
      {
        heading: "Como verificar um código",
        paragraphs: [
          "Antes de confiar em um cupom, verifique se ele ainda é válido e se se aplica ao que você está comprando.",
        ],
        items: [
          "Confirme que o código vem de uma fonte oficial.",
          "Verifique a data de validade.",
          "Leia os requisitos de elegibilidade e valor mínimo.",
          "Aplique-o no pagamento e confirme que o desconto aparece.",
        ],
      },
      {
        heading: "O que evitar",
        paragraphs: [
          "Evite códigos de sites de terceiros não confiáveis que prometem economias garantidas. Eles podem estar desatualizados, inválidos ou enganosos. Limite-se a fontes oficiais e verifique cada código antes de usá-lo.",
        ],
      },
    ],
    faq: [
      {
        q: "O Basetao tem cupons ativos?",
        a: "Ofertas podem aparecer de tempos em tempos, mas a disponibilidade muda. Consulte a plataforma oficial para as mais recentes.",
      },
      {
        q: "Como uso um código de desconto?",
        a: "Insira o código no campo apropriado no pagamento ou na sua conta e confirme que o desconto foi aplicado.",
      },
      {
        q: "Os códigos expiram?",
        a: "Sim, a maioria dos códigos tem data de validade e condições específicas, então verifique antes de usá-los.",
      },
      {
        q: "Posso combinar vários códigos?",
        a: "Geralmente não. Consulte as condições de cada oferta, pois a maioria não pode ser acumulada.",
      },
    ],
    conclusion:
      "Os cupons e códigos do Basetao podem oferecer economias reais quando estão disponíveis. A chave é usar fontes oficiais, verificar cada código e entender as condições para nunca depender de uma oferta expirada ou inválida.",
  },

  "is-basetao-legit": {
    slug: "is-basetao-legit",
    title: "O Basetao é legítimo? Guia de segurança e confiança",
    description:
      "Um olhar equilibrado sobre se o Basetao é legítimo, como avaliar a segurança e os passos que você pode dar para se proteger ao comprar.",
    intro: [
      "Se você está considerando o Basetao pela primeira vez, é natural perguntar se o serviço é legítimo e seguro. A resposta curta é que o Basetao é um agente de compras conhecido, mas a segurança sempre depende de quão cuidadoso você é ao comprar.",
      "Este guia explica como avaliar a confiança, quais riscos observar e os passos práticos que você pode dar para se proteger.",
    ],
    takeaways: [
      "O Basetao é um agente de compras reconhecido, mas você deve sempre verificar os detalhes por conta própria.",
      "Verifique a segurança do site, os métodos de pagamento e as políticas de reembolso antes de se comprometer.",
      "Pesquise vendedores e produtos em vez de confiar apenas no serviço.",
      "Proteja sua conta e suas informações pessoais o tempo todo.",
    ],
    sections: [
      {
        heading: "O Basetao é legítimo?",
        paragraphs: [
          "O Basetao é um agente de compras de longa data que muitos compradores internacionais usam para comprar produtos de marketplaces chineses. Ele oferece realização de pedidos, armazenagem, QC e envio. Como com qualquer agente, a legitimidade do serviço é separada da confiabilidade de vendedores e produtos individuais, por isso a verificação pessoal importa.",
        ],
      },
      {
        heading: "Como avaliar a segurança",
        paragraphs: [
          "Em vez de buscar um simples sim ou não, avalie um agente de compras por vários fatores concretos.",
        ],
        items: [
          "Segurança do site — verifique HTTPS e um domínio legítimo.",
          "Métodos de pagamento — use opções seguras e rastreáveis.",
          "Política de reembolso — entenda como funcionam reembolsos e devoluções.",
          "Informações do vendedor — verifique os vendedores de quem planeja comprar.",
          "Detalhes do produto — confirme a exatidão antes de pedir.",
          "Segurança da conta — use uma senha forte e única.",
        ],
      },
      {
        heading: "Sinais de alerta a observar",
        paragraphs: [
          "Tenha cuidado se sentir pressão para pagar rápido, condições de reembolso pouco claras, solicitações de pagamento suspeitas ou vendedores que evitam perguntas. Serviços e vendedores legítimos costumam responder a perguntas e fornecer informações claras.",
        ],
      },
      {
        heading: "Proteger suas informações",
        paragraphs: [
          "Use uma senha forte e única para sua conta, evite compartilhar credenciais e tenha cuidado com informações pessoais. Insira dados sensíveis apenas no site oficial, nunca por meio de links ou mensagens não solicitadas.",
        ],
      },
    ],
    faq: [
      {
        q: "O Basetao é seguro de usar?",
        a: "O Basetao é um serviço reconhecido, mas a segurança depende de verificar vendedores, produtos e políticas por conta própria antes de pedir.",
      },
      {
        q: "Como sei se um vendedor é confiável?",
        a: "Procure anúncios precisos, comunicação clara e feedback positivo de outros compradores.",
      },
      {
        q: "Quais métodos de pagamento o Basetao aceita?",
        a: "As opções de pagamento podem mudar, então consulte a plataforma oficial para a lista atual.",
      },
      {
        q: "O que fazer se algo der errado?",
        a: "Contate o suporte do Basetao imediatamente e guarde registros do seu pedido, pagamentos e comunicação.",
      },
    ],
    conclusion:
      "O Basetao é um agente de compras legítimo, mas a confiança se constrói por meio de verificação cuidadosa, não por suposições. Ao verificar segurança, pagamento, políticas e vendedores por conta própria, você compra com muito mais confiança.",
  },

  "basetao-review": {
    slug: "basetao-review",
    title: "Avaliação do Basetao: o que você deve saber antes de usá-lo",
    description:
      "Uma avaliação honesta e equilibrada do Basetao que cobre como funciona, seus pontos fortes e limitações, e para quem é mais adequado.",
    intro: [
      "Esta avaliação oferece um olhar equilibrado sobre o Basetao, um agente de compras que ajuda compradores internacionais a comprar produtos de marketplaces chineses. Ela cobre como o serviço funciona, o que faz bem, onde tem limitações e quem mais se beneficia.",
      "O objetivo é dar uma visão honesta para que você decida se o Basetao se encaixa nas suas necessidades, sem exageros de marketing nem alegações sem fundamento.",
    ],
    takeaways: [
      "O Basetao atua como uma camada intermediária para pedidos, armazenagem, QC e envio.",
      "É mais útil para compradores que querem ajuda com produtos de marketplaces chineses.",
      "O serviço tem pontos fortes claros, mas exige verificação pessoal cuidadosa.",
      "A adequação depende do seu orçamento, destino e do que planeja comprar.",
    ],
    sections: [
      {
        heading: "Como o Basetao funciona",
        paragraphs: [
          "O Basetao faz pedidos em seu nome, recebe itens em seu armazém, oferece fotos de controle de qualidade opcionais e envia suas compras internacionalmente. O Basetao Spreadsheet é um recurso complementar que organiza produtos e referências para facilitar a pesquisa.",
        ],
      },
      {
        heading: "Pontos fortes",
        paragraphs: [
          "Os principais pontos fortes do Basetao vêm de centralizar um processo que, de outra forma, seria fragmentado e demorado.",
        ],
        items: [
          "Pedidos e armazenagem centralizados.",
          "Fotos QC opcionais antes do envio.",
          "Consolidação de vários itens em um envio.",
          "Um spreadsheet estruturado para pesquisa de produtos.",
        ],
      },
      {
        heading: "Limitações",
        paragraphs: [
          "Como qualquer serviço, o Basetao tem limitações que valem a pena conhecer antes de começar.",
        ],
        items: [
          "Os custos de envio variam e podem se acumular.",
          "O serviço não garante a qualidade do vendedor nem do produto.",
          "Os preços e a disponibilidade mudam com frequência.",
          "Alguns processos exigem paciência e atenção aos detalhes.",
        ],
      },
      {
        heading: "Para quem pode ser adequado",
        paragraphs: [
          "O Basetao tende a atender compradores que querem ajuda para adquirir produtos de marketplaces chineses, especialmente aqueles que compram roupas, calçados e acessórios e valorizam fotos QC e consolidação. É menos ideal para quem precisa de entrega imediata ou prefere cuidar de cada etapa por conta própria.",
        ],
      },
    ],
    faq: [
      {
        q: "Vale a pena usar o Basetao?",
        a: "Pode valer, especialmente se você comprar vários itens e valorizar fotos QC e consolidação. O valor depende das suas necessidades específicas.",
      },
      {
        q: "Quais são as desvantagens?",
        a: "Os custos de envio variam, a qualidade depende dos vendedores e o processo exige um pouco de paciência.",
      },
      {
        q: "Quem deve usar o Basetao?",
        a: "Compradores que querem ajuda para pedir em marketplaces chineses e valorizam armazenagem, QC e consolidação.",
      },
      {
        q: "O Basetao garante qualidade?",
        a: "Não. O serviço não garante a qualidade do vendedor nem do produto, então você deve verificar os itens por conta própria.",
      },
    ],
    conclusion:
      "O Basetao é uma ferramenta prática para compradores que querem uma forma centralizada de pedir em marketplaces chineses, com recursos úteis como fotos QC e consolidação. Não é perfeito e exige verificação pessoal, mas para o comprador certo simplifica um processo complexo.",
  },
};